/* eslint-disable no-console */
import {useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {useRecoilCallback} from 'recoil';

import {APIError} from 'data/errors';
import {HttpStatusCode} from 'data/protocols/http';
import {TypeLocalNameEnum} from 'domain/usecases';
import {GetHealthDistrictsParams} from 'domain/usecases/private/booking-flow/health/get-health-districts';
import {
	AuthorizationsEnum,
	FeaturesEnum,
	QueryParamsForTaggingEnum,
	TelehealthBookingEnum,
} from 'presentation/constants';
import {useHostName} from 'presentation/hooks';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useGetHomeQuery} from 'presentation/pages/private/home/query/useGetHomeQuery';
import {ExamSpecialty, HealthSpecialty, Specialty} from 'presentation/utils';

import {useQueryParams} from '../../../hooks';
import {EntryPointCodeEnum, FlowEnum, TELEMEDICINE_INFO, TelemedicineEnum} from '../../constants';
import {scheduledBookingState} from '../../pages/SpecialtySelection/atoms';

import {useExamOrientationsInfoStore} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog/store/useExamOrientationsInfoStore';
import {ExamInfo} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog/store/useExamOrientationsInfoStore/types';
import {useExamOrientationsModalStore} from 'presentation/modules/booking/exam/ui/pages/ExamBooking/steps/ExamSelection/components/ExamOrientationsDialog/store/useExamOrientationsModalStore';
import {useBookingFlowStepsStore} from 'presentation/modules/booking/shared/store/useBookingFlowStepsStore';
import {useBookingStore} from 'presentation/modules/booking/shared/store/useBookingStore';

import {ScheduledBooking} from '../../pages/SpecialtySelection/types';

import {TEA_SPECIALTY_CODE} from './constants';
import {CheckBookingModal, UseBookingSpecialtyProps} from './types';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {useBookingAuthorization} from 'presentation/modules/booking/shared/hooks/useBookingAuthorization';
import {useGetHealthDistrictsQuery} from 'presentation/modules/booking/health/ui/pages/HealthBooking/steps/HealthDistrictSelection/queries/useGetHealthDistrictsQuery';
import {makeGetHealthSpecialtyEligibilityScript} from 'main/factories/usecases/private/booking-flow/remote-health-booking/get-health-specialty-eligibility-script-factory';
import {useTeaForm} from 'presentation/modules/booking/shared/hooks/useTeaForm';
import {getTelehealthStepPosition} from 'presentation/modules/booking/telehealth/utils/getTelehealthStepPosition';
import {ONLY_TELEMEDICINE_SPECIALTIES_CODE} from 'presentation/modules/booking/shared/constants/onlyTelemedicineSpecialties';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';

export function useBookingSpecialty({
	flowType,
	handleNext,
}: UseBookingSpecialtyProps) {
	const {isGndiSaoPaulo} = useHostName();
	const bookingData = useBookingStore(state => state.bookingData);
	const setBookingData = useBookingStore(state => state.setBookingData);
	const setActiveBookingFlowStep = useBookingFlowStepsStore(state => state.setBookingStep);

	const isExam = flowType === FlowEnum.EXAM;
	const isTelehealth = flowType === FlowEnum.TELEHEALTH;
	const isOdonto = flowType === FlowEnum.ODONTO;
	const isHealth = flowType === FlowEnum.HEALTH;

	const navigate = useNavigate();
	const {clearSearch} = useQueryParams();

	const {data: appointmentsList, refetch} = useGetHomeQuery();

	const {verifyExamAuthorization, verifyHealthAuthorization} = useBookingAuthorization();
	const isHealthManualLocation = bookingData.isManualLocation && isHealth;
	const [checkBookingModal, setCheckBookingModal] = useState<CheckBookingModal>('none');
	const [scheduledBooking, setScheduledBooking] = useState<ScheduledBooking | null>(null);
	const examOrientationsModal = useExamOrientationsModalStore(state => state.isOpen);
	const setExamOrientationsModal = useExamOrientationsModalStore(state => state.setIsOpen);
	const setExamOrientationsInfo = useExamOrientationsInfoStore(
		state => state.setExamOrientationInfo,
	);
	const examOrientationsInfo = useExamOrientationsInfoStore(state => state.examOrientationInfo);
	const [isSelectedExamAuthRequired, setIsSelectedExamAuthRequired] = useState(false);
	const [examSelected, setExamSelected] = useState('');

	const {handleOpenEmbeddedInfo} = useTeaForm();

	const {PROFESSIONAL_STEP} = getTelehealthStepPosition();

	const {isEnabled: isOnlyTelemedicineSpecialtyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SPECIALTY_ONLY_TELEMEDICINE_CHIP,
	);

	const checkDuplicity = useRecoilCallback(
		({snapshot, refresh}) =>
			async (specialty_code: string) => {
				refresh(
					scheduledBookingState({
						flowType,
						specialty_code,
						cityName: bookingData.cityName,
						ufCode: bookingData.ufCode,
						appointmentsList: appointmentsList?.appointments ?? [],
					}),
				);
				return snapshot.getPromise(
					scheduledBookingState({
						flowType,
						specialty_code,
						cityName: bookingData.cityName,
						ufCode: bookingData.ufCode,
						appointmentsList: appointmentsList?.appointments ?? [],
					}),
				);
			},
		[appointmentsList],
	);

	async function handleChooseScriptSuggestion() {
		try {
			setCheckBookingModal('verifyingDuplicity');
			const alreadyScheduled = await checkDuplicity(String(bookingData.specialtyCode));
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				setCheckBookingModal('duplicityState');
				return;
			}
			handleNext();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	const handleNavigateToExamAuth = () => {
		navigate(AuthorizationsEnum.AUTHORIZATIONS);
	};

	const handleCloseExamAuthRequiredDrawer = () => {
		setIsSelectedExamAuthRequired(false);
		setExamSelected('');
	};

	async function handleSelectCardExam(specialty: Specialty) {
		setExamSelected(specialty.formattedText);
		try {
			if ((specialty as ExamSpecialty).flAutorizacao) {
				setCheckBookingModal('verifyingAuth');
				const {autorizaAgendamento} = await verifyExamAuthorization({
					cdTipoExame: Number(specialty.code),
					cdPontoEntrada: Number(EntryPointCodeEnum.V1),
				});
				setCheckBookingModal('none');

				if (!autorizaAgendamento) {
					setIsSelectedExamAuthRequired(true);

					return;
				}
			}

			setCheckBookingModal('verifyingDuplicity');
			const alreadyScheduled = await checkDuplicity(specialty.code);
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				setCheckBookingModal('duplicityState');
				return;
			}

			setExamOrientationsInfo({
				code: specialty.code,
				examName: specialty.formattedText,
				orientations: (specialty as ExamSpecialty).dsPreparo,
			});

			setExamOrientationsModal(true);
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	function handleAgreeExamOrientations(examInfo: ExamInfo) {
		setBookingData(prev => ({
			...prev,
			specialtyCode: examInfo.code,
			specialtyName: examInfo.examName,
			professionalCode: '',
			professionalName: '',
			unitCode: '',
			unitName: '',
			unitAddress: '',
			dateAt: '',
			hourAt: '',
		}));

		clearSearch();
		setExamOrientationsModal(false);
		handleNext();
	}

	async function handleSelectCardOdonto(specialty: Specialty) {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		try {
			setCheckBookingModal('verifyingDuplicity');
			const alreadyScheduled = await checkDuplicity(specialty.code);
			setCheckBookingModal('none');

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
				});
				setCheckBookingModal('duplicityState');
				return;
			}

			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
				professionalCode: '',
				professionalName: '',
				unitCode: '',
				unitName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
				districtName: '',
				odontoRecommendations: '',
			}));

			clearSearch();
			handleNext();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	async function handleSelectCardTelehealth(specialty: Specialty) {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		if (isGndiSaoPaulo && specialty.code === TEA_SPECIALTY_CODE) {
			handleOpenEmbeddedInfo();
			return;
		}

		try {
			const permitMultipleBookings = (specialty as HealthSpecialty).multipleBookings;

			setCheckBookingModal('verifyingDuplicity');
			const alreadyScheduled = permitMultipleBookings
				? false
				: await checkDuplicity(specialty.code);

			setCheckBookingModal('verifyingAuth');
			const {
				content: {specialtyAuthorization},
			} = await verifyHealthAuthorization({
				speciality: specialty.code,
			});
			setCheckBookingModal('none');

			if (!specialtyAuthorization) {
				setCheckBookingModal('needAuth');
				return;
			}

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
				});
				setCheckBookingModal('duplicityState');
				return;
			}

			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
				unitCode: TELEMEDICINE_INFO.code,
				unitName: TELEMEDICINE_INFO.name,
				professionalCode: '',
				professionalName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
				multipleBookings: (specialty as HealthSpecialty).multipleBookings,
			}));

			clearSearch();
			handleNext();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	const districtParams: GetHealthDistrictsParams = {
		stateCode: bookingData.ufCode.toUpperCase(),
		locationName: bookingData.cityName.toUpperCase(),
		locationType:
			bookingData.typeLocalName === TypeLocalNameEnum.COORDENADAS
				? TypeLocalNameEnum.CIDADE
				: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
		specialtyCode: Number(bookingData.specialtyCode),
	};

	const {refetch: refetchDistrictList} = useGetHealthDistrictsQuery({
		params: districtParams,
		enabled: false,
	});

	async function handleVerifyAuthorization(specialtyCode: string) {
		try {
			const {
				content: {specialtyAuthorization, suggestedSpecialties, smartScheduleSpecialty},
			} = await verifyHealthAuthorization({
				speciality: specialtyCode,
				city: isTelehealth ? undefined : bookingData.cityName,
				uf: isTelehealth ? undefined : bookingData.ufCode,
			});

			if (smartScheduleSpecialty && suggestedSpecialties && suggestedSpecialties.length > 0) {
				const [firstSuggestedSpecialty] = suggestedSpecialties;
				setBookingData(prev => ({
					...prev,
					specialtyCode: String(firstSuggestedSpecialty.code),
					specialtyName: String(firstSuggestedSpecialty.description),
				}));

				setCheckBookingModal('scriptState');
				return false;
			}

			if (!specialtyAuthorization) {
				setCheckBookingModal('needAuth');
				return false;
			}

			return true;
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			throw error;
		}
	}

	async function schedulerScript() {
		const currentSearchParams = new URLSearchParams(window.location.search);
		setBookingData(prev => ({
			...prev,
			isScheduleScript: true,
			bookingOrigin: BookingOriginEnum.BOOKING_SCRIPT,
		}));
		currentSearchParams.set(
			QueryParamsForTaggingEnum.BOOKING_ORIGIN,
			BookingOriginEnum.BOOKING_SCRIPT,
		);
	}

	async function schedulerDefault(specialty: Specialty) {
		try {
			setBookingData(prev => ({
				...prev,
				specialtyCode: specialty.code,
				specialtyName: specialty.formattedText,
				professionalCode: '',
				professionalName: '',
				unitCode: '',
				unitName: '',
				unitAddress: '',
				dateAt: '',
				hourAt: '',
			}));

			if ('sub_code' in specialty && specialty.sub_code) {
				setBookingData(prev => ({
					...prev,
					subSpecialtyCode: specialty.sub_code,
				}));
			} else {
				setBookingData(prev => ({
					...prev,
					subSpecialtyCode: '',
				}));
			}

			if (isHealthManualLocation) {
				setCheckBookingModal('verifyingDistricts');
				const hasTelehealth = specialty.telemedicine === TelemedicineEnum.IS_TELEMEDICINE;
				try {
					const {data: districtList} = await refetchDistrictList({throwOnError: true});
					setCheckBookingModal('none');
					if (hasTelehealth && districtList?.length === 0) {
						setActiveBookingFlowStep(value => value + 1);
					}
					clearSearch();
					handleNext();
				} catch (error) {
					console.error(error);
					setCheckBookingModal('none');
					const {statusCode} = error as APIError;
					const areDistrictsEmpty = statusCode === HttpStatusCode.unprocessable;

					if (!areDistrictsEmpty) {
						setCheckBookingModal('error');
						return;
					}

					if (hasTelehealth) {
						setActiveBookingFlowStep(value => value + 1);
						handleNext();
						return;
					}

					handleNext();
				}
				return;
			}

			clearSearch();
			handleNext();
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	async function handleSelectCardHealthV2(specialty: Specialty) {
		const hasSubSpecialty = 'sub_code' in specialty && specialty.sub_code;

		const originalLocationData = {
			coordinates: bookingData.coordinates,
			ufCode: bookingData.ufCode,
			ufName: bookingData.ufName,
			cityName: bookingData.cityName,
			typeLocalName: bookingData.typeLocalName,
		};

		setBookingData(prev => ({
			...prev,
			specialtyCode: specialty.code,
			subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
			specialtyName: specialty.formattedText,
			hasTelehealth: specialty.telemedicine === TelemedicineEnum.IS_TELEMEDICINE,
			shouldBackToSpecialtyStep: false,
		}));

		const getHealthSpecialtyEligibilityParams = {
			specialtyCode: specialty.code,
			stateCode: bookingData.ufCode,
			locationName: bookingData.cityName,
			locationType:
				bookingData.typeLocalName === TypeLocalNameEnum.COORDENADAS
					? TypeLocalNameEnum.CIDADE
					: bookingData.typeLocalName || TypeLocalNameEnum.CIDADE,
			telemedicine: '',
			entryPointCode: EntryPointCodeEnum.V2,
		};

		try {
			setCheckBookingModal('verifyingAuth');
			const alreadyScheduled = await checkDuplicity(specialty.code);

			if (alreadyScheduled) {
				setScheduledBooking({
					...alreadyScheduled,
					subSpecialtyCode: hasSubSpecialty ? specialty.sub_code : '',
					specialtyNameSelected: specialty.formattedText,
				});
				setCheckBookingModal('duplicityState');
				return;
			}

			const isAuthorized = await handleVerifyAuthorization(specialty.code);
			if (!isAuthorized) return;

			if (
				isOnlyTelemedicineSpecialtyEnabled &&
				ONLY_TELEMEDICINE_SPECIALTIES_CODE.includes(specialty.code)
			) {
				setBookingData(prev => ({
					...prev,
					unitCode: TELEMEDICINE_INFO.code,
					unitName: TELEMEDICINE_INFO.name,
					unitAddress: '',
					ufCode: TELEMEDICINE_INFO.ufCode,
					ufName: TELEMEDICINE_INFO.ufName,
					cityName: TELEMEDICINE_INFO.cityName,
					bookingRedirect: true,
					bookingType: BookingTypeEnum.TELEHEALTH,
					hourAt: '',
					dateAt: '',
					professionalCode: '',
					professionalName: '',
					typeLocalName: TypeLocalNameEnum.CIDADE,
					isManualLocation: true,
					initialBookingLocationData: {
						coordinates: originalLocationData.coordinates,
						ufCode: originalLocationData.ufCode,
						ufName: originalLocationData.ufName,
						cityName: originalLocationData.cityName,
						typeLocalName:
							originalLocationData.typeLocalName || TypeLocalNameEnum.CIDADE,
						bookingType: BookingTypeEnum.HEALTH,
					},
				}));
				setActiveBookingFlowStep(PROFESSIONAL_STEP);
				navigate(TelehealthBookingEnum.TELEHEALTH_BOOKING_FLOW);
				clearSearch();

				return;
			}

			const {
				content: {isEligible: isEligibleForSchedulerScript},
			} = await makeGetHealthSpecialtyEligibilityScript().get(
				getHealthSpecialtyEligibilityParams,
			);
			setCheckBookingModal('none');

			if (isEligibleForSchedulerScript || alreadyScheduled) {
				await schedulerScript();
			} else {
				await schedulerDefault(specialty);
			}
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
		}
	}

	function handleSelectCard(speciality: Specialty) {
		refetch();
		if (isExam) return handleSelectCardExam(speciality);
		if (isOdonto) return handleSelectCardOdonto(speciality);
		if (isTelehealth) return handleSelectCardTelehealth(speciality);
		return handleSelectCardHealthV2(speciality);
	}

	const handleCheckDuplicity = async (specialtyCode: string) => {
		try {
			const alreadyScheduled = await checkDuplicity(specialtyCode);
			setCheckBookingModal('none');
			if (alreadyScheduled) {
				setScheduledBooking(alreadyScheduled);
				setCheckBookingModal('duplicityState');
				return true;
			}
			return false;
		} catch (error) {
			console.error(error);
			setCheckBookingModal('error');
			return true;
		}
	};

	return {
		checkBookingModal,
		setCheckBookingModal,
		handleSelectCard,
		scheduledBooking,
		handleChooseScriptSuggestion,
		handleAgreeExamOrientations,
		examOrientationsModal,
		setExamOrientationsModal,
		examOrientationsInfo,
		handleCheckDuplicity,
		isSelectedExamAuthRequired,
		handleNavigateToExamAuth,
		examSelected,
		handleCloseExamAuthRequiredDrawer,
	};
}
